// The ladder (DESIGN.md § The ladder).
//
// Six rungs, and **no rung's concepts exist in the interface until that
// rung is reached**. There is no settings page where you switch them on;
// the app notices the need and offers the tool at the moment the need
// appears. An unlocked rung that goes unused for sixty days offers to
// tuck itself away again.
//
// Pure functions over an operation's history. No UI, no storage, no
// clock of its own — `now` is always passed in. The fleet app owns
// persistence and decides when to ask; this file only ever answers three
// questions: which rung is reached, what should be offered next, and what
// can collapse.

import { profileFor, type ProfileId } from "./profiles.js";

export type Rung = 0 | 1 | 2 | 3 | 4 | 5;

export const RUNGS: readonly Rung[] = Object.freeze([0, 1, 2, 3, 4, 5]);

/** The concepts each rung adds. Nothing above the reached rung is rendered. */
export const RUNG_CONCEPTS: Readonly<Record<Rung, readonly string[]>> = Object.freeze({
  0: ["address", "driver", "link"],
  1: ["ordering", "per-stop progress", "per-stop arrival estimate"],
  2: ["roster", "sealing", "handover", "day view"],
  3: ["routes", "terms", "day certificates", "lasting links"],
  4: ["offers", "bids", "award verification"],
  5: ["fleet seed", "zones", "traffic contribution"]
});

const DAY = 86400;

/** Sixty days unused, and a rung offers to tuck itself away. */
export const COLLAPSE_AFTER_SECONDS = 60 * DAY;

/** Rung 3's window: the same stop set on three days inside two weeks. */
export const REPEAT_WINDOW_SECONDS = 14 * DAY;
export const REPEAT_DAYS_NEEDED = 3;

/** Rung 4: a job created with no driver available, twice. */
export const DRIVERLESS_NEEDED = 2;

/** Rung 5: five or more devices enrolled. */
export const DEVICES_FOR_MESH = 5;

/** Rung 2: a second device card, or two jobs running at once. */
export const DEVICES_FOR_ROSTER = 2;

export interface JobRecord {
  id: string;
  createdUnixSeconds: number;
  /** How many stops the job was dispatched with. Two is rung 1. */
  stopCount: number;
  /**
   * An identity for the *stop set*, so "the same round three days
   * running" is a comparison and not a guess. The fleet app derives it;
   * `planRef` hex is the obvious choice.
   */
  planKey?: string | null;
  /** The device this job was sealed to, when it was sealed to one. */
  deviceId?: string | null;
  /** DESIGN rung 4: created with no driver available. */
  driverless?: boolean;
  startedUnixSeconds?: number | null;
  finishedUnixSeconds?: number | null;
  /**
   * Which rungs' concepts this job actually used — an offer was run, a
   * handover happened, it was dispatched as a route. This is what keeps
   * a rung alive; the collapse rule reads it and nothing else.
   */
  usedRungs?: readonly Rung[];
}

export interface DeviceRecord {
  id: string;
  enrolledUnixSeconds: number;
  lastSeenUnixSeconds?: number | null;
  retiredUnixSeconds?: number | null;
}

export interface OperationHistory {
  profile: ProfileId;
  jobs: readonly JobRecord[];
  devices: readonly DeviceRecord[];
  /** Rungs the operator asked for outright. DESIGN allows this for rung 5. */
  operatorRequested?: readonly Rung[];
  /** Rungs the operator has tucked away since. They stay away until re-earned. */
  collapsed?: readonly Rung[];
}

export interface RungEvidence {
  rung: Rung;
  reached: boolean;
  /** One sentence, in the app's own voice, naming what did it. */
  why: string | null;
  /** When the evidence completed, so collapse has something to count from. */
  atUnixSeconds: number | null;
}

/** What the app should put in front of the operator, and why now. */
export interface Offer {
  kind: "unlock" | "collapse";
  rung: Rung;
  /** Words a person would say — the fleet app may use these verbatim. */
  title: string;
  body: string;
}

function activeDevices(history: OperationHistory, now: number): DeviceRecord[] {
  return history.devices.filter(device =>
    device.enrolledUnixSeconds <= now
    && (device.retiredUnixSeconds == null || device.retiredUnixSeconds > now));
}

function jobsUpTo(history: OperationHistory, now: number): JobRecord[] {
  return history.jobs
    .filter(job => job.createdUnixSeconds <= now)
    .slice()
    .sort((a, b) => a.createdUnixSeconds - b.createdUnixSeconds);
}

/** Two jobs whose live windows overlap: the moment one phone stops being enough. */
function concurrentJobs(jobs: JobRecord[]): number | null {
  const spans = jobs
    .map(job => ({
      from: job.startedUnixSeconds ?? job.createdUnixSeconds,
      to: job.finishedUnixSeconds ?? Infinity
    }))
    .sort((a, b) => a.from - b.from);
  for (let i = 1; i < spans.length; i++) {
    const current = spans[i]!;
    for (let j = 0; j < i; j++) {
      const earlier = spans[j]!;
      if (earlier.to > current.from) return current.from;
    }
  }
  return null;
}

/** The same stop set dispatched on three separate days inside two weeks. */
function repeatedRound(jobs: JobRecord[]): { planKey: string; atUnixSeconds: number } | null {
  const byPlan = new Map<string, number[]>();
  for (const job of jobs) {
    if (!job.planKey) continue;
    const day = Math.floor(job.createdUnixSeconds / DAY);
    const days = byPlan.get(job.planKey) ?? [];
    if (!days.includes(day)) days.push(day);
    byPlan.set(job.planKey, days);
  }
  for (const [planKey, days] of byPlan) {
    days.sort((a, b) => a - b);
    for (let i = REPEAT_DAYS_NEEDED - 1; i < days.length; i++) {
      const last = days[i]!;
      const first = days[i - (REPEAT_DAYS_NEEDED - 1)]!;
      if ((last - first) * DAY <= REPEAT_WINDOW_SECONDS) {
        return { planKey, atUnixSeconds: (last + 1) * DAY };
      }
    }
  }
  return null;
}

/**
 * Every rung, with the evidence for it. Rung 0 is always reached: an
 * address, a driver and a link is the whole application.
 */
export function rungEvidence(history: OperationHistory, now: number): RungEvidence[] {
  const jobs = jobsUpTo(history, now);
  const devices = activeDevices(history, now);
  const profile = profileFor(history.profile);
  const requested = new Set(history.operatorRequested ?? []);
  const evidence: RungEvidence[] = [];

  evidence.push({
    rung: 0,
    reached: true,
    why: "An address, someone to take it there, and a link.",
    atUnixSeconds: jobs[0]?.createdUnixSeconds ?? null
  });

  const multiStop = jobs.find(job => job.stopCount >= 2) ?? null;
  evidence.push({
    rung: 1,
    reached: multiStop != null,
    why: multiStop ? "A second stop was added to one job." : null,
    atUnixSeconds: multiStop?.createdUnixSeconds ?? null
  });

  const secondDevice = devices.length >= DEVICES_FOR_ROSTER
    ? devices.slice().sort((a, b) => a.enrolledUnixSeconds - b.enrolledUnixSeconds)[DEVICES_FOR_ROSTER - 1]!
    : null;
  const overlap = concurrentJobs(jobs);
  const rung2At = [secondDevice?.enrolledUnixSeconds, overlap]
    .filter((value): value is number => value != null)
    .sort((a, b) => a - b)[0] ?? null;
  evidence.push({
    rung: 2,
    reached: rung2At != null,
    why: rung2At == null
      ? null
      : (secondDevice && secondDevice.enrolledUnixSeconds === rung2At
        ? "A second device was enrolled."
        : "Two jobs ran at the same time."),
    atUnixSeconds: rung2At
  });

  // School transport is the noted exception: a route is the only object
  // that makes sense there, so the profile grants the rung outright.
  const repeat = repeatedRound(jobs);
  const schoolGrant = profile.defaults.grantsRung === 3;
  const rung3At = schoolGrant
    ? (jobs[0]?.createdUnixSeconds ?? now)
    : (repeat?.atUnixSeconds ?? null);
  evidence.push({
    rung: 3,
    reached: schoolGrant || repeat != null,
    why: schoolGrant
      ? "School transport runs on routes; there is no other shape for a term."
      : (repeat ? "The same round has gone out three days inside a fortnight." : null),
    atUnixSeconds: rung3At
  });

  const driverless = jobs.filter(job => job.driverless === true);
  const rung4At = driverless.length >= DRIVERLESS_NEEDED
    ? driverless[DRIVERLESS_NEEDED - 1]!.createdUnixSeconds
    : null;
  evidence.push({
    rung: 4,
    reached: rung4At != null,
    why: rung4At == null ? null : "Twice now, a job was created with nobody free to take it.",
    atUnixSeconds: rung4At
  });

  const fifthDevice = devices.length >= DEVICES_FOR_MESH
    ? devices.slice().sort((a, b) => a.enrolledUnixSeconds - b.enrolledUnixSeconds)[DEVICES_FOR_MESH - 1]!
    : null;
  const rung5At = fifthDevice?.enrolledUnixSeconds ?? (requested.has(5) ? now : null);
  evidence.push({
    rung: 5,
    reached: rung5At != null,
    why: rung5At == null
      ? null
      : (fifthDevice ? "Five devices are enrolled — the mesh is worth something now." : "You asked for it."),
    atUnixSeconds: rung5At
  });

  return evidence;
}

/**
 * Which rungs the interface may show concepts from.
 *
 * A rung the operator tucked away stays away until it is earned again —
 * `collapsed` is checked against the evidence timestamp, so a collapsed
 * rung whose evidence is fresher than the collapse comes back.
 */
export function unlockedRungs(history: OperationHistory, now: number): Rung[] {
  const collapsed = new Set(history.collapsed ?? []);
  return rungEvidence(history, now)
    .filter(entry => entry.reached && !collapsed.has(entry.rung))
    .map(entry => entry.rung);
}

/** The highest rung reached. Rung 0 always. */
export function reachedRung(history: OperationHistory, now: number): Rung {
  const unlocked = unlockedRungs(history, now);
  return unlocked.length ? unlocked[unlocked.length - 1]! : 0;
}

/** Whether one rung's concepts belong in the interface at all. */
export function rungUnlocked(history: OperationHistory, now: number, rung: Rung): boolean {
  return unlockedRungs(history, now).includes(rung);
}

/** The concepts the interface may name right now, flattened. */
export function availableConcepts(history: OperationHistory, now: number): string[] {
  return unlockedRungs(history, now).flatMap(rung => [...RUNG_CONCEPTS[rung]]);
}

/**
 * When a rung was last *used*, from the jobs that used it. A rung nobody
 * has exercised since it unlocked counts from the unlock.
 */
function lastUsed(history: OperationHistory, rung: Rung, evidence: RungEvidence[]): number | null {
  let latest: number | null = null;
  for (const job of history.jobs) {
    if (!job.usedRungs?.includes(rung)) continue;
    const at = job.finishedUnixSeconds ?? job.createdUnixSeconds;
    if (latest == null || at > latest) latest = at;
  }
  if (latest != null) return latest;
  return evidence.find(entry => entry.rung === rung)?.atUnixSeconds ?? null;
}

/**
 * Rungs unlocked, unused for sixty days, and therefore offering to tuck
 * themselves away. Rungs 0 and 1 never collapse — an address and a
 * sequence are not vocabulary anyone carries reluctantly.
 */
export function collapsibleRungs(history: OperationHistory, now: number): Rung[] {
  const evidence = rungEvidence(history, now);
  const collapsed = new Set(history.collapsed ?? []);
  const out: Rung[] = [];
  for (const rung of [2, 3, 4, 5] as Rung[]) {
    if (collapsed.has(rung)) continue;
    const entry = evidence.find(item => item.rung === rung);
    if (!entry?.reached) continue;
    // School transport is granted rung 3 by its profile; it cannot go
    // unused, because there is nothing else for the operation to be.
    if (rung === 3 && profileFor(history.profile).defaults.grantsRung === 3) continue;
    const used = lastUsed(history, rung, evidence);
    if (used == null) continue;
    if (now - used >= COLLAPSE_AFTER_SECONDS) out.push(rung);
  }
  return out;
}

const UNLOCK_COPY: Readonly<Record<Rung, { title: string; body: string }>> = Object.freeze({
  0: { title: "Send it", body: "An address, someone to take it there, and a link for the customer." },
  1: {
    title: "That is a round now",
    body: "Two stops, so the app will order them and start saying which one it is on."
  },
  2: {
    title: "More than one phone",
    body: "Jobs can be sealed to a driver's device now, and handed over mid-shift."
  },
  3: {
    title: "Make it a route?",
    body: "You have sent this round three days running. A route keeps its link, and the driver can change."
  },
  4: {
    title: "Ask someone you do not employ",
    body: "Twice now there was nobody free. You can broadcast what a job is worth without saying where it goes."
  },
  5: {
    title: "Run your own seed",
    body: "Five phones is a mesh. Your drivers can reach each other without going through anyone else."
  }
});

const COLLAPSE_COPY: Readonly<Record<Rung, { title: string; body: string }>> = Object.freeze({
  0: { title: "", body: "" },
  1: { title: "", body: "" },
  2: { title: "Put the roster away?", body: "One phone has done all the work for two months." },
  3: { title: "Put routes away?", body: "Nothing has run as a route in two months." },
  4: { title: "Put offers away?", body: "You tried offers once and have not needed them since." },
  5: { title: "Put the fleet seed away?", body: "Nothing has used your own seed in two months." }
});

/**
 * What the app should offer next — one thing, or nothing.
 *
 * `acknowledged` is the set of rungs the operator has already been shown.
 * It is a parameter rather than state because this file owns no storage:
 * the fleet app persists what it has said, and this function only decides
 * what is worth saying. Unlocks come before collapses — a new tool at the
 * moment the need appears is the product, and tidying up can wait a day.
 */
export function nextOffer(
  history: OperationHistory,
  now: number,
  acknowledged: readonly Rung[] = []
): Offer | null {
  const [unlock] = pendingUnlocks(history, now, acknowledged);
  if (unlock) return unlock;
  const [collapsible] = collapsibleRungs(history, now);
  if (collapsible != null) {
    const copy = COLLAPSE_COPY[collapsible];
    return { kind: "collapse", rung: collapsible, title: copy.title, body: copy.body };
  }
  return null;
}

/**
 * Every unlock the operation has newly earned since it last looked, so a
 * caller that persists `acknowledged` can show each one exactly once.
 */
export function pendingUnlocks(
  history: OperationHistory,
  now: number,
  acknowledged: readonly Rung[] = []
): Offer[] {
  const known = new Set([...acknowledged, ...(history.collapsed ?? [])]);
  return rungEvidence(history, now)
    .filter(entry => entry.reached && entry.rung !== 0 && !known.has(entry.rung))
    .map(entry => ({
      kind: "unlock" as const,
      rung: entry.rung,
      title: UNLOCK_COPY[entry.rung].title,
      body: UNLOCK_COPY[entry.rung].body
    }));
}
